import { Box, Button, Card, Stack, Typography, useTheme } from '@mui/material';
import { ReactElement, useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useRecoilState, useRecoilValue } from 'recoil';

import { updateOrder } from '@app/adapter/order-service';
import { createFollow, getFollows } from '@app/adapter/organization-service';
import { CancelModal } from '@app/components/Order/CancelModal';
import { WorkResultModal } from '@app/components/Order/WorkResultModal';
import { CardPartOrganization } from '@app/components/Product/CardPartOrganization';
import { CardPartTitle } from '@app/components/Product/CardPartTitle';
import { userAuthInfoSelector } from '@app/domain/app';
import {
  attributesByClinicalDepartmentSelector,
  attributesByJobTypeSelector,
} from '@app/domain/catalog';
import { orderCountState } from '@app/domain/top-nav';
import { useSetSnackbar } from '@app/hooks/useSetSnackbar';
import { Product } from '@app/types/catalog';
import { Order, OrderStatus, OrderUpdate } from '@app/types/order';
import { FollowerType, FollowType } from '@app/types/organization';
import {
  getProductClinicalDeptLabel,
  getProductJobTypeLabel,
} from '@app/utils/catalog';
import { isError } from '@app/utils/error';
import { canOrderCancel, canOrderWorkResult } from '@app/utils/order';

interface OrderListProps {
  items: Order[];
}

export function OrderList({ items }: OrderListProps): ReactElement {
  const theme = useTheme();
  const navigate = useNavigate();
  const setSnackbar = useSetSnackbar();
  const authInfo = useRecoilValue(userAuthInfoSelector);
  const attributesByClinicalDepartment = useRecoilValue(
    attributesByClinicalDepartmentSelector
  );
  const attributesByJobType = useRecoilValue(attributesByJobTypeSelector);
  const [navBadgeCount, setNavBadgeCount] = useRecoilState(orderCountState);
  const [isWorkResultModal, setIsWorkResultModal] = useState<boolean>(false);
  const [isCancelModal, setIsCancelModal] = useState<boolean>(false);
  const [selectedOrder, setSelectedOrder] = useState<Order>();
  const [orders, setOrders] = useState<Order[]>([]);

  useEffect(() => {
    setOrders(items);
  }, [items]);

  const getProduct = useCallback((order: Order) => {
    return order.lineItems[0].product as Product;
  }, []);

  const cancelButtonLabel = useCallback((order: Order) => {
    return order.status === OrderStatus.PENDING ? '応募' : '勤務';
  }, []);

  const clinicalDeptLabel = useCallback(
    (order: Order) => {
      const product = getProduct(order);
      if (!product || !attributesByClinicalDepartment.length) return '';
      return getProductClinicalDeptLabel(
        attributesByClinicalDepartment,
        product
      );
    },
    [attributesByClinicalDepartment, getProduct]
  );
  const jobTypeLabel = useCallback(
    (order: Order) => {
      const product = getProduct(order);
      if (!product || !attributesByJobType.length) return '';
      return getProductJobTypeLabel(attributesByJobType, product);
    },
    [attributesByJobType, getProduct]
  );

  const handleClickWorkResult = useCallback(
    (event: React.MouseEvent<HTMLElement>, order: Order) => {
      event.stopPropagation();
      setSelectedOrder(order);
      setIsWorkResultModal(true);
    },
    []
  );

  const checkFollow = useCallback(
    async (organizationId: string) => {
      if (!authInfo) return false;

      try {
        const follows = await getFollows(
          authInfo.accessToken,
          authInfo.fingerPrint,
          FollowerType.USER,
          authInfo.userId,
          {
            followId: organizationId,
            followType: FollowType.ORGANIZATION,
            limit: 1,
          }
        );
        return follows.data.total === 0;
      } catch (e) {
        if (isError(e)) {
          throw new Error(e.message);
        }
        return false;
      }
    },
    [authInfo]
  );

  const updateOrderToWorkResult = useCallback(
    async (
      organizationId: string,
      orderId: string,
      payload: OrderUpdate,
      isFollow?: boolean
    ) => {
      if (!authInfo) return;
      const userId = authInfo?.userId;
      const token = authInfo?.accessToken;
      const fingerprint = authInfo?.fingerPrint;

      try {
        await updateOrder(token, fingerprint, organizationId, orderId, payload);
        if (isFollow && (await checkFollow(organizationId))) {
          await createFollow(organizationId, token, fingerprint, {
            followerId: userId,
            followerType: FollowerType.USER,
          });
        }
        setSnackbar(true, '勤務完了しました', 'success');
        setOrders(
          orders.map((order) => {
            return order.id === orderId ? { ...order, ...payload } : order;
          })
        );
        setIsWorkResultModal(false);
      } catch (e) {
        if (isError(e)) {
          console.error(e.message);
        }
        setSnackbar(true, '更新に失敗しました');
      }
    },
    [authInfo, checkFollow, orders, setSnackbar]
  );

  const handleClickCancel = useCallback(
    (event: React.MouseEvent<HTMLElement>, order: Order) => {
      event.stopPropagation();
      setSelectedOrder(order);
      setIsCancelModal(true);
    },
    []
  );

  const updateOrderToCancel = useCallback(
    async (organizationId: string, orderId: string, payload: OrderUpdate) => {
      if (!authInfo) return;
      try {
        await updateOrder(
          authInfo.accessToken,
          authInfo.fingerPrint,
          organizationId,
          orderId,
          payload
        );
        setSnackbar(true, 'キャンセルしました', 'success');

        const order = orders.find((order) => order.id === orderId);
        if (order?.status === OrderStatus.PENDING) {
          setNavBadgeCount(navBadgeCount - 1);
        }

        setOrders(
          orders.map((order) => {
            return order.id === orderId ? { ...order, ...payload } : order;
          })
        );
        setIsCancelModal(false);
      } catch (e) {
        if (isError(e)) {
          console.error(e.message);
        }
        setSnackbar(true, 'キャンセルできませんでした');
      }
    },
    [authInfo, navBadgeCount, orders, setSnackbar, setNavBadgeCount]
  );

  return (
    <Stack
      spacing={{ md: 2, xs: 1 }}
      sx={{ backgroundColor: theme.palette.neutral.greyLight }}
    >
      <Box></Box>
      {orders.map((item) => {
        const product = getProduct(item);
        return (
          <Card
            key={item.id}
            sx={{ borderRadius: 0, cursor: 'pointer', p: 2 }}
            onClick={() => navigate(`/orders/${item.id}`)}
          >
            <Stack spacing={1}>
              {product && <CardPartTitle product={product} />}
              <Typography variant="body2" component="div">
                <Box>{clinicalDeptLabel(item)}</Box>
                <Box>{jobTypeLabel(item)}</Box>
              </Typography>
              {typeof item.organization === 'object' && (
                <CardPartOrganization organization={item.organization} />
              )}
              {canOrderWorkResult(item) && (
                <Button
                  color="orange"
                  type="submit"
                  variant="contained"
                  fullWidth
                  onClick={(e) => handleClickWorkResult(e, item)}
                >
                  勤務しました
                </Button>
              )}
              {canOrderCancel(item) && (
                <Button
                  color="primary"
                  type="submit"
                  variant="outlined"
                  fullWidth
                  onClick={(e) => handleClickCancel(e, item)}
                >
                  {`${cancelButtonLabel(item)}をキャンセル`}
                </Button>
              )}
            </Stack>
          </Card>
        );
      })}
      <WorkResultModal
        order={selectedOrder}
        open={isWorkResultModal}
        onClose={() => setIsWorkResultModal(false)}
        onSubmit={updateOrderToWorkResult}
      />
      <CancelModal
        order={selectedOrder}
        open={isCancelModal}
        onClose={() => setIsCancelModal(false)}
        onSubmit={updateOrderToCancel}
      />
    </Stack>
  );
}
